import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth0 } from 'Auth';
import { useLazyIncentiveProgramsApi } from 'Shared/services/Api';
import { useRewardsEnrollmentContext } from 'Shared/providers/RewardsEnrollmentProvider';
import { reportEvent } from 'Shared/analytics';
import { env } from 'Shared/constants/env';
import {
  OutcomeConfig,
  outcomesForPhase,
} from 'Rewards/services/endpoints/GetRewardsConfig/types';
import { GetAgentStatusResponse } from 'Rewards/services/endpoints/GetAgentStatus/types';
import { PostAttemptResponse } from 'Rewards/services/endpoints/PostAttempt/types';
import { layoutPanelsForOutcome, SCRATCH_SYMBOLS } from './scratchUtils';

// ─── State machine ────────────────────────────────────────────────────────────
//
//  loading  ──► idle        (config + status fetched, entries available)
//  loading  ──► no_entries  (config + status fetched, entries exhausted)
//  loading  ──► error       (API error on initial load)
//  idle     ──► revealed    (all 9 panels scratched or Reveal All clicked)
//  revealed ──► posting     (user clicks Scratch Another Card)
//  posting  ──► idle        (POST /attempts resolves; new pending outcome ready)

export type ScratchState =
  | 'loading'
  | 'posting'
  | 'idle'
  | 'revealed'
  | 'no_entries'
  | 'error';

export type UseScratchOffReturn = {
  status: GetAgentStatusResponse | null;
  outcomes: OutcomeConfig[];
  scratchState: ScratchState;
  panels: string[];
  revealedPanels: boolean[];
  pendingResult: PostAttemptResponse | null;
  pendingOutcomeConfig: OutcomeConfig | null;
  attemptError: boolean;
  hasRemainingEntries: boolean;
  remainingEntries: number;
  revealPanel: (index: number) => void;
  revealAll: () => void;
  dismissOutcome: () => void;
  startNewAttempt: () => void;
};

const MOCK_REWARDS =
  (process.env.NODE_ENV !== 'production' &&
    process.env.REACT_APP_MOCK_REWARDS === 'true') ||
  env.rewardsMockEnabled;

let mockOutcomeCursor = 0;

const EMPTY_PANELS = Array(9).fill('');
const EMPTY_REVEALED = Array(9).fill(false);

export function useScratchOff(): UseScratchOffReturn {
  const history = useHistory();
  const { user } = useAuth0();
  const agentId = user!.id;

  const {
    config: configFromContext,
    status: statusFromContext,
    pendingAttempt,
    setPendingAttempt,
    rewardsDataLoading,
    rewardsDataError,
    loadRewardsData,
    refetchStatus: refetchStatusFromContext,
  } = useRewardsEnrollmentContext();

  useEffect(() => {
    loadRewardsData();
  }, [loadRewardsData]);

  const [scratchState, setScratchState] = useState<ScratchState>('loading');
  const [pendingResult, setPendingResult] =
    useState<PostAttemptResponse | null>(null);
  const [pendingOutcomeConfig, setPendingOutcomeConfig] =
    useState<OutcomeConfig | null>(null);
  const [panels, setPanels] = useState<string[]>(EMPTY_PANELS);
  const [revealedPanels, setRevealedPanels] =
    useState<boolean[]>(EMPTY_REVEALED);
  const [attemptError, setAttemptError] = useState(false);

  // Tracks whether PUT /accept has fired for the current card. Reset on each
  // new card so idempotency is card-scoped, not session-scoped.
  const acceptFiredRef = useRef(false);

  const [, executeNewAttempt] =
    useLazyIncentiveProgramsApi<PostAttemptResponse>({
      method: 'POST',
      url: `/agents/${agentId}/attempts`,
    });

  const [, executeAccept] = useLazyIncentiveProgramsApi<unknown>({
    method: 'PUT',
  });

  const acceptAttempt = useCallback(
    (attemptId: string | null) => {
      if (MOCK_REWARDS || !attemptId) return;
      executeAccept({
        url: `/agents/${agentId}/attempts/${attemptId}/accept`,
      }).catch(() => {
        reportEvent('26for26-scratch-off:accept-failed');
      });
    },
    [agentId, executeAccept]
  );

  const config = configFromContext;
  const status = statusFromContext;
  const dataLoading = rewardsDataLoading;
  const dataError = rewardsDataError;

  const outcomes: OutcomeConfig[] = useMemo(
    () => (config && status ? outcomesForPhase(config, status.phase.code) : []),
    [config, status]
  );

  // ── Transition out of 'loading' once initial data is available ─────────────
  useEffect(() => {
    if (dataLoading) return;
    if (dataError) {
      setScratchState('error');
      return;
    }
    if (!config || !status) return;
    if (scratchState !== 'loading') return;

    // Happy path: outer "Play Scratch-off" button seeded pendingAttempt in
    // context. Fallback: deep-link/reconnect — re-derive from status.
    let pending: PostAttemptResponse | null = pendingAttempt;
    if (!pending && status.hasActiveAttempt && status.lastResult) {
      pending = {
        rewardAttemptId: status.activeAttemptId ?? '',
        status: 'PENDING',
        outcome: status.lastResult.outcome,
        prizeCode: status.lastResult.prizeCode,
        prizeAmount: status.lastResult.prizeAmount,
        payoutStatus: null,
        raffleEntryNumber: null,
        consumedBucket: null,
      };
    }

    if (pending) {
      const outcomeIndex = outcomes.findIndex(
        (o) =>
          o.code === pending!.outcome && o.prizeAmount === pending!.prizeAmount
      );
      const outcomeConfig = outcomeIndex >= 0 ? outcomes[outcomeIndex] : null;
      const outcomeCode = pending.outcome ?? 'NONE';
      setPendingResult(pending);
      setPendingOutcomeConfig(outcomeConfig);
      setPanels(layoutPanelsForOutcome(outcomeCode, SCRATCH_SYMBOLS));
      setRevealedPanels(Array(9).fill(false));
      acceptFiredRef.current = false;
      setScratchState('idle');
      return;
    }

    // No pending attempt and no active attempt from /status — redirect to /rewards
    // rather than rendering a blank playable grid. SpinWheel has the same gap at L215.
    history.replace('/rewards');
  }, [
    config,
    dataError,
    dataLoading,
    history,
    outcomes,
    pendingAttempt,
    scratchState,
    status,
  ]);

  // ── Reveal individual panel ────────────────────────────────────────────────
  const revealPanel = useCallback(
    (index: number) => {
      if (scratchState !== 'idle' || revealedPanels[index]) return;

      const next = [...revealedPanels];
      next[index] = true;
      setRevealedPanels(next);

      // Fire accept on the first scratch; ignore subsequent scratches.
      if (!acceptFiredRef.current) {
        acceptFiredRef.current = true;
        acceptAttempt(pendingResult?.rewardAttemptId ?? null);
      }

      if (next.every(Boolean)) {
        setScratchState('revealed');
      }
    },
    [acceptAttempt, pendingResult, revealedPanels, scratchState]
  );

  // ── Reveal All ────────────────────────────────────────────────────────────
  const revealAll = useCallback(() => {
    if (scratchState !== 'idle') return;

    if (!acceptFiredRef.current) {
      acceptFiredRef.current = true;
      acceptAttempt(pendingResult?.rewardAttemptId ?? null);
    }

    setRevealedPanels(Array(9).fill(true));
    setScratchState('revealed');
  }, [acceptAttempt, pendingResult, scratchState]);

  // ── Remaining entries (mirror useSpinWheel idiom) ─────────────────────────
  const remainingEntries = Math.max(0, (status?.availableEntries ?? 0) - 1);
  const hasRemainingEntries = remainingEntries > 0;

  // ── Dismiss outcome modal ─────────────────────────────────────────────────
  const dismissOutcome = useCallback(() => {
    setPendingResult(null);
    setPendingOutcomeConfig(null);
    setPendingAttempt(null);
    setPanels(EMPTY_PANELS);
    setRevealedPanels(EMPTY_REVEALED);
    acceptFiredRef.current = false;
    setScratchState(hasRemainingEntries ? 'idle' : 'no_entries');
    if (!MOCK_REWARDS) refetchStatusFromContext();
  }, [hasRemainingEntries, refetchStatusFromContext, setPendingAttempt]);

  // ── Start a new attempt (Scratch Another Card) ────────────────────────────
  const startNewAttempt = useCallback(() => {
    setPendingResult(null);
    setPendingOutcomeConfig(null);
    setPanels(EMPTY_PANELS);
    setRevealedPanels(EMPTY_REVEALED);
    acceptFiredRef.current = false;
    setAttemptError(false);
    setScratchState('posting');

    if (MOCK_REWARDS) {
      const currentIndex = mockOutcomeCursor % Math.max(outcomes.length, 1);
      const outcome = outcomes[currentIndex];
      mockOutcomeCursor += 1;
      if (outcome) {
        const mockResult: PostAttemptResponse = {
          rewardAttemptId: `mock-scratch-${mockOutcomeCursor}`,
          status: 'DECIDED',
          outcome: outcome.code,
          prizeCode: outcome.isWin ? outcome.code : null,
          prizeAmount: outcome.prizeAmount,
          payoutStatus: null,
          raffleEntryNumber: null,
          consumedBucket: null,
        };
        setPendingResult(mockResult);
        setPendingOutcomeConfig(outcome);
        setPanels(layoutPanelsForOutcome(outcome.code, SCRATCH_SYMBOLS));
      }
      setScratchState('idle');
      return;
    }

    executeNewAttempt({ data: {} })
      .then(({ data: result }) => {
        const outcomeIndex = outcomes.findIndex(
          (o) =>
            o.code === result.outcome && o.prizeAmount === result.prizeAmount
        );
        const pending: PostAttemptResponse = {
          rewardAttemptId: result.rewardAttemptId,
          status: 'PENDING',
          outcome: result.outcome,
          prizeCode: result.prizeCode,
          prizeAmount: result.prizeAmount,
          payoutStatus: null,
          raffleEntryNumber: null,
          consumedBucket: null,
        };
        setPendingResult(pending);
        setPendingAttempt(pending);
        setPendingOutcomeConfig(
          outcomeIndex >= 0 ? outcomes[outcomeIndex] : null
        );
        setPanels(
          layoutPanelsForOutcome(result.outcome ?? 'NONE', SCRATCH_SYMBOLS)
        );
        setRevealedPanels(Array(9).fill(false));
        setScratchState('idle');
        refetchStatusFromContext();
      })
      .catch(() => {
        setAttemptError(true);
        setScratchState('idle');
      });
  }, [
    executeNewAttempt,
    outcomes,
    refetchStatusFromContext,
    setPendingAttempt,
  ]);

  return {
    status,
    outcomes,
    scratchState,
    panels,
    revealedPanels,
    pendingResult,
    pendingOutcomeConfig,
    attemptError,
    hasRemainingEntries,
    remainingEntries,
    revealPanel,
    revealAll,
    dismissOutcome,
    startNewAttempt,
  };
}
